/* eslint-disable @typescript-eslint/no-unused-vars */
/***********************************************************************
ChemicalResults.tsx - To Show the Chemical Results and Permeation Data
************************************************************************/
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import "./ChemicalResults.scss";
import Breadcrumb from '../../../common/Breadcrumb/Breadcrumb';
import { RiDownloadLine } from 'react-icons/ri';
import { ImgExplanation, ImgNoGloves } from '../../../../components/dashboard/pages/glovesSelector/glovesSelection';
import Table from 'react-bootstrap/Table';
import Modal from 'react-bootstrap/Modal';
import { useTranslation } from "react-i18next";
import { useGetPermeationsQuery } from "../../../../services/getPermeations";
import Loader from "../../../common/Loader/Loader";
import FilterFacets from "../filterFacets/filterFacets";
import { setCalculator } from "../../../../store/actions";
import jsPDF from "jspdf";
import { addAnalytics, sendProductData } from "../../../common/functions/utils";
import { kclRootState } from "../../../../store/gloveSelectorStore";
import ExplanationModal from "./showExplanationModal/ShowExplanationModal";
import { chemicalList, chemicalProductList, facetFltersChemicalQuery } from "../../../../types/allTypes";
import BlobImage from "../../../common/Image/BlobImage";
import { PERMEATIONS_REQ_QUERY, CHEMICAL_RESULTS_TABLE_LABEL_HEAD, CHEMICAL_RESULTS_TABLE_HEAD, CHEMICAL_RESULTS_TABLE_IMAGE_HEAD, CHEMICAL_RESULTS_TABLE_DATA } from "../../../../helper/constants";
import { utilAnalytics } from "../../../common/analytics/analyticsUtil";
const ChemicalResults = () => {
    const language = useSelector((state: kclRootState) => state?.glovesSelector?.language || "en");
    const filterFacets = useSelector((state: kclRootState) => state?.glovesSelector?.setFilterFacets || {});
    const { t } = useTranslation();
    const selectedChemicals = useSelector((state: kclRootState) => state?.glovesSelector?.calculator?.selectedChemicals || []);
    const { FACET_FLOWS } = useSelector((state: kclRootState) => state?.glovesSelector?.config || {});
    const [facetQueryFilters, setFacetQueryFilters] = useState<facetFltersChemicalQuery>([]);
    let languageSelectedForProducts: string = "English (GB)";
    if (language == "de") {
        languageSelectedForProducts = "German";
    } else {
        languageSelectedForProducts = "English (GB)";
    }
    const styleNumbersData = useSelector((state: kclRootState) => state?.glovesSelector?.calculator?.styleNumberArray || []);
    const permeationReq = PERMEATIONS_REQ_QUERY(selectedChemicals, languageSelectedForProducts, styleNumbersData);
    //updating payload with facet query
    let objKeyLength = Object.keys(facetQueryFilters)?.length;
    if (facetQueryFilters && objKeyLength > 0) {
        permeationReq.searchQuery.filters.all = [...permeationReq.searchQuery.filters.all, ...facetQueryFilters];
    }
    const { data, isLoading } = useGetPermeationsQuery({
        payload: permeationReq,
        language: language
    });
    const [loader, showLoader] = useState<boolean>(false);
    const [showDownloadProgress, setShowDownloadProgress] = useState<boolean>(false);
    const [showCases, setShowCases] = useState<boolean>(false);
    const [showChemicalCase, setShowChemicalCase] = useState<string>('');
    const [casNumbers, setcasNumbers] = useState<string[]>([]);
    const [LevelsBool, setLevelsBool] = useState<boolean>(true);
    const [permeationProducts, setPermeationProducts] = useState<chemicalProductList>([]);
    const [productCount, setProductCount] = useState<number>();
    const [permeationBreakThroughTimes, setPermeationBreakThroughTimes] = useState<chemicalList[]>([]);
    const [displayItems, setDisplayItems] = useState<number>(5);
    const [show, setShow] = useState<boolean>(false);
    const closeExplanation = () => setShow(false);
    const showExplanation = () => setShow(true);

    useEffect(() => {
        setPermeationProducts(data?.result?.data?.productList);
        setPermeationBreakThroughTimes(data?.result?.data?.chemicalList);
        setProductCount(data?.result?.data?.totalRecords);
        if (filterFacets.length !== undefined) {
            sendSelectedFacetsToAnalytics(filterFacets, data?.result?.data?.totalRecords);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const sendSelectedFacetsToAnalytics = (filterData, totalRecords) => {
        const filterAnalyticData = new Array();
        filterData?.forEach((arrayItem) => {
            const entries = Object.entries(arrayItem);
            for (const [key, value] of entries) {
                let newValue: string[] = value as Array<string>;
                const newKey: string = key;
                const filterDataValue = newValue.toString().replaceAll(",", "|");
                filterAnalyticData.push({ [`${newKey}`]: filterDataValue });
            }
        });
        filterAnalyticData.push({ "totalRecords": totalRecords || 0 });
        const customData = {
            "event": "FiltersSelected",
            "filterData": filterAnalyticData
        };
        addAnalytics(customData);
    };
    const permeationsData = useMemo(() => {
        return (permeationBreakThroughTimes?.slice(0, displayItems === 0 ? 5 : displayItems));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayItems, permeationProducts]);

    // setting setFacetQueryFilters
    const setSearchFilters = useCallback((filters) => {
        setFacetQueryFilters(filters);
    }, [setFacetQueryFilters]);
    const casNumberLogic = (casNumberArray: string, chemicalName: string) => {
        const casArray: string[] = casNumberArray?.split(",");
        const showArray: string[] = [];
        for (let i = 1; i <= casArray?.length; i++) {
            showArray?.push(casArray[i]);
        }
        setcasNumbers(showArray);
        setShowChemicalCase(chemicalName);
        setShowCases(!showCases);
    };
    const minLevelToggle = () => {
        let minLevel = "Level";
        setLevelsBool(!LevelsBool);
        if (LevelsBool === false) {
            minLevel = "Minute";
        }
        setCalculator(!LevelsBool ? "Minutes" : "Levels");
        const customData = {
            "event": "minutesLevelToggle",
            toggleName: minLevel
        };
        addAnalytics(customData);
        utilAnalytics(
            true,
            {
                event: "minutesLevelToggle",
                _honeywellinternationalinc:
                {
                    toggle:
                    {
                        toggleName: minLevel,
                        toggleClicked:
                        {
                            value: 1
                        }
                    }

                }

            },
            "/chemical-results"
        );

    };
    const showFirstCasNumber = (casNumber) => {
        const casNumberArray = casNumber?.split(",");
        if (casNumberArray?.length > 1) {
            return <div> <span>{casNumberArray[0]} </span> <span> + {casNumberArray?.length - 1}more</span></div>;
        }
        else {
            return <div> <span>{casNumberArray[0]}</span></div>;
        }

    };
    const loadMore = () => {
        setDisplayItems(displayItems + 5);
    };
    const removeAllChildNodes = (parent) => {
        while (parent.firstChild) {
            parent.removeChild(parent.firstChild);
        }
    };
    const getResultsPDF = async () => {
        const pdfWrapper = window.document.getElementById("results-tab");
        await removeAllChildNodes(pdfWrapper);
        const pdfResultPage = window.document.getElementsByClassName("results-table-pdf")[0]?.cloneNode(true);
        pdfWrapper?.appendChild(pdfResultPage);
        const pdfResultBodyPage = window.document.getElementsByClassName("results-table-body-pdf")[0]?.cloneNode(true) as HTMLElement;
        pdfResultBodyPage.classList.remove('hidden-pdf');
        const pdfBodyWrapper = window.document.getElementById("results-tab");
        pdfBodyWrapper?.appendChild(pdfResultBodyPage);
    };
    const downloadPDF = () => {
        try {
            setShowDownloadProgress(true);
            const doc = new jsPDF('p', 'px', [2000, 3100], true);
            getResultsPDF();
            const finalPage: HTMLElement = window.document.querySelector(".gloves-resultsPdf-page1") as HTMLElement;
            const element: HTMLElement = finalPage.getElementsByClassName('pdf-word-spacing')[0] as HTMLElement;
            element.style.wordSpacing = '4px';
            doc.html(finalPage, {
                callback: function (doc) {
                    doc.save(t("kclGloves.DownloadPDF"));
                    setShowDownloadProgress(false);
                }
            }).catch(() => {
                setShowDownloadProgress(false);
            });
        }
        catch {
            setShowDownloadProgress(false);
        }
    };
    const showProductWithPDF = (URL, productId) => {
        if (URL !== "") {
            return (
                <a className="pdfClickableLink" target="_blank" rel="noopener noreferrer" href={URL}>{productId}</a>
            );
        } else {
            return (
                <span className="productText">{productId}</span>
            );
        }
    };
    const sendAdobeAnalytics = (product, id) => {
        utilAnalytics(
            true,
            {
                event: "styleNumberClicked",
                _honeywellinternationalinc:
                {
                    styleNumber:
                    {
                        styleNumberClicked: id,
                        styleClicked:
                        {
                            value: 1
                        }

                    }

                }

            },
            "/chemical-search"
        );

    }
    const showLevelsMinutes = (data) => {
        if (data) {
            return (
                data
            );
        }
        else {
            return (
                "NA"
            );
        }
    };
    const chemicalListTable = (item, index) => {
        return (
            <>
                <tr key={index}>
                    <td className="chemical-first-col">
                        {item?.chemicalName} <div>{item?.physicalState} <span className="more-items" onClick={() => casNumberLogic(item?.chemCombiCas, item?.chemicalName)}>{showFirstCasNumber(item?.chemCombiCas)}</span></div>
                        {showCases && item?.chemicalName === showChemicalCase && (
                            <div className="show-items">
                                <ul>
                                    {casNumbers.map((val, index) => (
                                        <li key={index}>{val}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </td>
                    {productCount === 0 ? (<>
                        {index === 0 &&
                            <td colSpan={7} rowSpan={permeationBreakThroughTimes?.length} className="no-gloves-panel">
                                <div className="no-gloves">
                                    <img src={ImgNoGloves} alt="no gloves matching your criteria" />
                                    <div className="bold-text">{t("kclGloves.FilterOptionWarning1")}</div>
                                    <div className="gray-text">{t("kclGloves.FilterOptionWarning2")}</div>
                                </div>
                            </td>
                        }
                    </>) : (<>
                        {CHEMICAL_RESULTS_TABLE_DATA?.map((tableData, key) => {
                            return (
                                <td key={key}>
                                    <div className={tableData?.fieldClassName}>
                                        {tableData?.fieldKeyArray?.map((value, key) => {
                                            return (
                                                <div key={key} className={tableData?.fieldColumnClassName}>{showLevelsMinutes(LevelsBool ? item?.productData?.find(data => data?.productFamily === value)?.minutes : item?.productData?.find(data => data?.productFamily === value)?.levels)}</div>
                                            );
                                        })}
                                    </div>
                                </td>
                            );
                        }
                        )}

                    </>)}

                </tr>
            </>
        );
    };
    return (
        <>
            <Breadcrumb />
            <div className="chemical-results">
                <div className='main-title'>
                    <div className="textstart">
                        <div className='heading'>{t("kclGloves.Permeation_Gloves")} </div><span>{productCount === 0 ? t("kclGloves.No_Results_Found") : <>{productCount} {t("kclGloves.Product")}</>} </span>
                    </div>
                    <div className="text-end cta-buttons">
                        <div onClick={minLevelToggle} className="toggle">
                            <span className={LevelsBool ? "minutes active" : "minutes"}>{t("kclGloves.Minutes")}</span>
                            <span className={LevelsBool ? "levels" : "levels active"}>{t("kclGloves.Levels")}</span>
                        </div>
                        <div className="button button-primary" onClick={showDownloadProgress ? undefined : downloadPDF}>{showDownloadProgress ? <> <Loader customClass="spinner-border" /></> : <><RiDownloadLine className="download-icon" /> {t("kclGloves.DOWNLOAD")}</>} </div>
                    </div>
                </div>
                {isLoading && <><Loader /></>}
                {!isLoading &&
                    <div className="row px-0 breakthrough-time">
                        <div className="col-md-2 col-12 gloves-filters">
                            <FilterFacets flow={FACET_FLOWS.CHEMICAL_RESULTS} setSearchFilters={setSearchFilters} maxFilters={15} />
                        </div>
                        <div className="col-md-10 col-12 pl-3" onClick={() => setShowCases(!showCases)}>
                            <div className="breakthrough">
                                {t("kclGloves.Showing_Permeation_Minutes")}{LevelsBool ? t("kclGloves.Minutes") : t("kclGloves.Levels")}<span onClick={showExplanation}><img src={ImgExplanation} alt="Explanation" />{t("kclGloves.Read_Explanation")}</span>
                            </div>
                            <div className="results-table pb-3">
                                <Table bordered responsive>
                                    <thead className="results-table-pdf">
                                        <tr className="thead1">
                                            {CHEMICAL_RESULTS_TABLE_LABEL_HEAD?.map((item, key) => {
                                                return (
                                                    <th key={key} className={item?.fieldClassName}>{t(item?.fieldKey)}</th>
                                                );
                                            })}
                                        </tr>
                                        <tr className="thead2">
                                            <th></th>
                                            {CHEMICAL_RESULTS_TABLE_IMAGE_HEAD?.map((item, key) => {
                                                return (
                                                    <th key={key} className={item?.tableHeadClassName}>
                                                        <div className={item?.fieldClassName}>
                                                            {item?.fieldKeyArray?.map((value, key) => {
                                                                return (
                                                                    <div key={key} className={item?.fieldColumnClassName}>
                                                                        <BlobImage url={item?.fieldImage[key]} key={item?.fieldKey} />
                                                                        <div>{value}</div>
                                                                    </div>
                                                                );
                                                            })
                                                            }
                                                        </div>
                                                    </th>
                                                );
                                            }
                                            )}
                                        </tr>
                                        <tr className="thead3">
                                            <th></th>
                                            {CHEMICAL_RESULTS_TABLE_HEAD?.map((item, key) => {
                                                return (
                                                    <th key={key}>
                                                        <div className={item?.fieldClassName}>
                                                            {item?.fieldKeyArray?.map((value, key) => {
                                                                return (
                                                                    item?.multipleColumns === false ?
                                                                        permeationProducts?.filter(data => data?.productFamily === value)?.length >= 1 ? (<>
                                                                            {permeationProducts?.find(data => data?.productFamily === value)?.productId?.map((data, key) => {
                                                                                return (
                                                                                    <div key={key} className="columns-2" onClick={() => { sendProductData(item?.fieldClickValue, data); sendAdobeAnalytics(item?.fieldClickValue, data) }}>{showProductWithPDF(permeationProducts?.find(data => data?.productFamily === value)?.pdfUrls[key], data)}</div>
                                                                                );
                                                                            })}
                                                                        </>) : (<>
                                                                            <div className="grey columns-2">{t("kclGloves.No_Gloves")}</div>
                                                                        </>)
                                                                        :
                                                                        <div className="columns-4" key={key}>
                                                                            <div className="info1">
                                                                                {permeationProducts?.filter(data => data?.productFamily === value)?.length >= 1 ? (<>
                                                                                    {permeationProducts?.find(data => data?.productFamily === value)?.productId?.map((data, key) => {
                                                                                        return (
                                                                                            <div key={key} className="columns-2" onClick={() => { sendProductData(item?.fieldClickValue, data); sendAdobeAnalytics(item?.fieldClickValue, data) }}>{showProductWithPDF(permeationProducts?.find(data => data?.productFamily === value)?.pdfUrls[key], data)}</div>
                                                                                        );
                                                                                    })}
                                                                                </>) : (<>
                                                                                    <div className="grey columns-2">{t("kclGloves.No_Gloves")}</div>
                                                                                </>)}
                                                                            </div>
                                                                        </div>
                                                                );
                                                            })
                                                            }
                                                        </div>
                                                    </th>
                                                );
                                            }
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {permeationsData?.map((item, index) => {
                                            return (chemicalListTable(item, index));
                                        })}
                                    </tbody>
                                    <tbody className="results-table-body-pdf hidden-pdf">
                                        {permeationBreakThroughTimes?.map((item, index) => {
                                            return (chemicalListTable(item, index));
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                            {displayItems < permeationBreakThroughTimes?.length && <div className="button button-outline" onClick={() => loadMore()}>{t("kclGloves.LOAD_MORE")}</div>}
                        </div>
                    </div>
                }
            </div>
            <div className="loader-save-wrapper">
                <Modal className="modal-loader-save" show={loader}>
                    <div className="loader-save-content">
                        <Loader message={"Loading"} />
                    </div>
                </Modal>
            </div>
            <ExplanationModal show={show} closeExplanation={closeExplanation} />
        </>
    );
};

export default ChemicalResults;
